import { FormGroup, FormControlLabel, Button, Switch, TextField, Typography, Icon, InputAdornment, CircularProgress, Snackbar, Checkbox, IconButton, Tooltip, Paper } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { AuthorizationNumberInput, CurrencyInput, FileUploadDialog, FixedLoadingIndicator, InvoiceInput } from 'components'
import { DocumentContext, ManagementContext } from 'context'
import { useValues } from 'hooks'
import { AIR_TICKET_AUTH_NUMBER, AUTHORIZATION_NUMBER_SIZES, ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT, EXPENSE_ITEMS, FIELDS, formatCurrency, getReimbursementInfoByElectronicInvoice, getSupportDocumentByAuthorizationNumberService, ID_CARD_LENGTH, INITIAL_REIMBURSEMENT, RUC_LENGTH, validateRUC } from 'lib'
import React from 'react'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import { IExpense, IMessageConfig, IRefund, IReimbursement } from 'types'
import moment from 'moment'
import 'moment/locale/es'
import { wsUploadXMLManually } from 'api/APIUploadXML'

moment.locale("es")

interface Props {
    onAdd: (reimbursement: IReimbursement) => void
    onClose: () => void
    loading?: boolean
    edit?: IReimbursement
    withDetails?: boolean
    initialValue?: Partial<IRefund>
    validation?: (reimbursement: IReimbursement) => boolean
    withExpenses?: boolean
    supplierIdentification: string
    supplierName?: string
    withTaxSupport?: boolean
    skipAuthorizationValidation?: boolean
    defaultCloseOnAdd?: boolean
    isCCH?: boolean
}

const ReimbursementForm = (props: Props) => {
    const { edit, onAdd, onClose, withDetails, initialValue, validation, withExpenses, supplierIdentification, supplierName, withTaxSupport, skipAuthorizationValidation, defaultCloseOnAdd, isCCH } = props
    const { costCenters } = React.useContext(DocumentContext)
    const { offices } = React.useContext(ManagementContext)
    const { updateValue: handleChange, values: reimbursement, updateValues } = useValues(edit ? {} : INITIAL_REIMBURSEMENT)
    const [closeOnAdd, setCloseOnAdd] = React.useState<boolean>(defaultCloseOnAdd ?? false)
    const [searching, setSearching] = React.useState<boolean>(false)
    const descriptionInput = React.useRef<any>(null)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info" })


    const [originalInvoiceTotal, setOriginalInvoiceTotal] = React.useState<number>(0)


    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (validation && !validation({ ...reimbursement })) {
            return
        }
        if (reimbursement[FIELDS.issuerIdentification.key] !== "5555555555555" && Boolean(reimbursement[FIELDS.taxSupport.key]) && !validateRUC(reimbursement[FIELDS.issuerIdentification.key] ?? "")) {
            setMessageConfig({ message: `Ingresa un RUC válido`, severity: "error", open: true })
            return
        }
        if (Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.authorizationNumber.key] && !AUTHORIZATION_NUMBER_SIZES.includes(reimbursement[FIELDS.authorizationNumber.key].length ?? -1) && !skipAuthorizationValidation) {
            setMessageConfig({ message: `El número de autorización debe constar entre ${AUTHORIZATION_NUMBER_SIZES.join(`, `)} dígitos`, severity: "error", open: true })
            return
        }
        if ((reimbursement[FIELDS.subtotalRateTwelve.key] == '0.00'
            || reimbursement[FIELDS.subtotalRateTwelve.key] == undefined
            || reimbursement[FIELDS.subtotalRateTwelve.key] == 0) &&
            (reimbursement[FIELDS.iva.key] !== '0.00' && reimbursement[FIELDS.iva.key] !== undefined && reimbursement[FIELDS.iva.key] !== 0)) {
            setMessageConfig({ message: `El valor del IVA debe ser 0 si el Subtotal IVA 12% es 0`, severity: "error", open: true })
            return
        }
        if (
            Boolean(reimbursement[FIELDS.taxSupport.key]) &&
            withExpenses &&
            reimbursement[FIELDS.receiverIdentification.key] &&
            (reimbursement[FIELDS.receiverIdentification.key].length !== RUC_LENGTH || reimbursement[FIELDS.receiverIdentification.key].length !== ID_CARD_LENGTH) && !supplierIdentification?.includes(reimbursement[FIELDS.receiverIdentification.key]) &&
            reimbursement[FIELDS.authorizationNumber.key] && reimbursement[FIELDS.authorizationNumber.key] !== AIR_TICKET_AUTH_NUMBER
        ) {
            setMessageConfig({ message: `La identificación del receptor no coincide con la identificación del proveedor seleccionado ${supplierIdentification}`, severity: "error", open: true })
            return
        }
        if (withExpenses && (!Boolean(reimbursement.expenses) || reimbursement.expenses?.length === 0)) {
            setMessageConfig({ message: `Agrega como mínimo un gasto`, severity: "error", open: true })
            return
        }
        if (Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.documentNumber.key].includes("_")) {
            setMessageConfig({ message: `El formato de número de documento es incorrecto`, severity: "error", open: true })
            return
        }
        if (!Boolean(reimbursement[FIELDS.emissionDate.key])) {
            setMessageConfig({ message: `Agregar fecha de emisión`, severity: "error", open: true })
            return
        }
        if (!Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.authorizationNumber.key] === undefined) {
            handleChange(reimbursement[FIELDS.authorizationNumber.key], "")
        }
        if (!Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.issuerIdentification.key] === undefined) {
            handleChange(reimbursement[FIELDS.issuerIdentification.key], "")
        }
        if (!Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.issuerName.key] === undefined) {
            handleChange(reimbursement[FIELDS.issuerName.key], "")
        }
        onAdd({
            ...reimbursement,
            iva: Boolean(reimbursement.iva) ? reimbursement.iva : 0,
            iva15: Boolean(reimbursement.iva15) ? reimbursement.iva15 : 0,
            subtotalNoTaxesObject: Boolean(reimbursement.subtotalNoTaxesObject) ? reimbursement.subtotalNoTaxesObject : 0,
            subtotalRateTwelve: Boolean(reimbursement.subtotalRateTwelve) ? reimbursement.subtotalRateTwelve : 0,
            subtotalRateQuince: Boolean(reimbursement.subtotalRateQuince) ? reimbursement.subtotalRateQuince : 0,
            subtotalRateZero: Boolean(reimbursement.subtotalRateZero) ? reimbursement.subtotalRateZero : 0
        })
        updateValues(INITIAL_REIMBURSEMENT)

        if (supplierIdentification && supplierName) {
            updateValues({
                [FIELDS.receiverIdentification.key]: supplierIdentification,
                [FIELDS.receiverName.key]: supplierName
            })
        }

        if (closeOnAdd) {
            onClose()
        }
    }
    const handleSearchDocument = async () => {
        if (reimbursement[FIELDS.authorizationNumber.key]?.length < ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT) {
            setMessageConfig({ message: `El número de autorización debe tener ${ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT} dígitos`, severity: "warning", open: true })
            return
        }
        try {
            setSearching(true)
            const result = await getSupportDocumentByAuthorizationNumberService(reimbursement[FIELDS.authorizationNumber.key])
            const electronicReimbursement = getReimbursementInfoByElectronicInvoice(result, result.electronicData)
            updateValues({ ...electronicReimbursement, taxSupport: Boolean(reimbursement[FIELDS.taxSupport.key]) })
            const originalTotal =
                parseFloat(String(electronicReimbursement.subtotalNoTaxesObject ?? "0")) +
                parseFloat(String(electronicReimbursement.subtotalRateZero ?? "0")) +
                parseFloat(String(electronicReimbursement.subtotalRateQuince ?? "0")) +
                parseFloat(String(electronicReimbursement.iva15 ?? "0"))

            setOriginalInvoiceTotal(originalTotal)
            setSearching(false)
            setMessageConfig({ message: "Documento encontrado", severity: "success", open: true })
        } catch (error: any) {
            setMessageConfig({ message: error.message, severity: "error", open: true })
            setSearching(false)
        }
    }

    const handleSearchDocumentByClaveAcceso = async (clavecceso: string) => {
        if (clavecceso.length < ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT) {
            setMessageConfig({ message: `El número de autorización debe tener ${ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT} dígitos`, severity: "warning", open: true })
            return
        }
        try {
            setSearching(true)
            const result = await getSupportDocumentByAuthorizationNumberService(clavecceso)
            const electronicReimbursement = getReimbursementInfoByElectronicInvoice(result, result.electronicData)
            updateValues({ ...electronicReimbursement, taxSupport: Boolean(reimbursement[FIELDS.taxSupport.key]) })
            const originalTotal =
                parseFloat(String(electronicReimbursement.subtotalNoTaxesObject ?? "0")) +
                parseFloat(String(electronicReimbursement.subtotalRateZero ?? "0")) +
                parseFloat(String(electronicReimbursement.subtotalRateQuince ?? "0")) +
                parseFloat(String(electronicReimbursement.iva15 ?? "0"))

            setOriginalInvoiceTotal(originalTotal)
            setSearching(false)
            setMessageConfig({ message: "Documento encontrado", severity: "success", open: true })
        } catch (error: any) {
            setMessageConfig({ message: error.message, severity: "error", open: true })
            setSearching(false)
        }
    }

    const getTotal = React.useCallback(() => {
        const subtotalNoTaxesObject = parseFloat(reimbursement[FIELDS.subtotalNoTaxesObject.key] ?? "0")
        const subtotalRateTwelve = parseFloat(reimbursement[FIELDS.subtotalRateTwelve.key] ?? "0")
        const subtotalRateQuince = parseFloat(reimbursement['subtotalRateQuince'] ?? "0")
        const subtotalRateZero = parseFloat(reimbursement[FIELDS.subtotalRateZero.key] ?? "0")
        const iva = parseFloat(reimbursement[FIELDS.iva.key] ?? "0")
        const iva15 = parseFloat(reimbursement['iva15'] ?? "0")
        return subtotalNoTaxesObject + subtotalRateTwelve + subtotalRateZero + iva + iva15 + subtotalRateQuince
    }, [reimbursement])


    //////////////////////////////// SUBIDA XML
    const [openDialog, setOpenDialog] = React.useState<boolean>(false)
    const [loading, setLoading] = React.useState(false)
    const [clavecceso, setclaveacceso] = React.useState<string | undefined>(undefined)
    const handleUpload = async (file: File) => {
        try {
            setLoading(true)
            const claveaccesoresponse: string = await wsUploadXMLManually(file)
            setclaveacceso(claveaccesoresponse)
            setLoading(false)
            setOpenDialog(false)
            setMessageConfig({ open: true, message: "El archivo cargado será procesado a continuación.", severity: "info" })
            handleSearchDocumentByClaveAcceso(claveaccesoresponse)
        } catch (error) {
            setLoading(false)
            setOpenDialog(false)
            setMessageConfig({ open: true, message: "El archivo cargado no pudo ser procesado.", severity: "error" })
        }
    }
    //////////////////////////////// SUBIDA XML


    const validateTotalLimit = (newValues: any) => {

        if (!Boolean(reimbursement[FIELDS.taxSupport.key])) {
            return true
        }

        const total =
            parseFloat(String(newValues.subtotalNoTaxesObject ?? "0")) +
            parseFloat(String(newValues.subtotalRateZero ?? "0")) +
            parseFloat(String(newValues.subtotalRateQuince ?? "0")) +
            parseFloat(String(newValues.iva15 ?? "0"))

        if (total > originalInvoiceTotal) {
            setMessageConfig({
                open: true,
                severity: "warning",
                message: `El total ingresado no puede superar el total original de la factura (${formatCurrency(originalInvoiceTotal)})`
            })

            return false
        }

        return true
    }


    React.useEffect(() => {
        const init = async () => {
            if (edit) {
                updateValues(edit)
            }
            else {
                updateValues({
                    authorizationNumber: initialValue?.authorizationDoc,
                    emissionDate: moment(initialValue?.emitionDate, "DD/MM/YYYY").toDate(),
                    issuerIdentification: initialValue?.identification,
                    documentNumber: initialValue?.sequential,
                    taxSupport: Boolean(initialValue?.taxSupport)
                })
            }
            if ((initialValue?.authorizationDoc?.length ?? 0) < ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT) {
                setSearching(false)
                return
            }
            try {
                setSearching(true)
                const result = await getSupportDocumentByAuthorizationNumberService(initialValue?.authorizationDoc)
                const electronicReimbursement = getReimbursementInfoByElectronicInvoice(result, result.electronicData)
                updateValues({ ...electronicReimbursement, authorizationNumber: initialValue?.authorizationDoc, taxSupport: true })
                const originalTotal =
                    parseFloat(String(electronicReimbursement.subtotalNoTaxesObject ?? "0")) +
                    parseFloat(String(electronicReimbursement.subtotalRateZero ?? "0")) +
                    parseFloat(String(electronicReimbursement.subtotalRateQuince ?? "0")) +
                    parseFloat(String(electronicReimbursement.iva15 ?? "0"))

                setOriginalInvoiceTotal(originalTotal)
                setSearching(false)
                setMessageConfig({ message: "Documento encontrado", severity: "success", open: true })
            } catch (error: any) {
                setMessageConfig({ message: error.message, severity: "error", open: true })
                setSearching(false)
            }
        }

        if (Boolean(edit)) {
            updateValues(edit)
        }
        if (initialValue?.authorizationDoc) {
            init()
        }
    }, [updateValues, initialValue])

    React.useEffect(() => {
        if (supplierIdentification && supplierName) {
            updateValues({
                [FIELDS.receiverIdentification.key]: supplierIdentification,
                [FIELDS.receiverName.key]: supplierName,
                ...edit
            })
        }
    }, [supplierIdentification, supplierName, edit])


    const ExpensesSummary = () => {
        if (!reimbursement[FIELDS.expenses.key]) return null;

        return (
            <Paper variant="outlined" className="mt-4 p-4 w-full">
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Resumen del Gasto</Typography>
                <div className="flex flex-col mt-2">
                    <div className="flex justify-between">
                        <Typography>Subtotal no Objeto:</Typography>
                        <Typography>{reimbursement[FIELDS.subtotalNoTaxesObject.key] ?? "0"}</Typography>
                    </div>
                    <div className="flex justify-between">
                        <Typography>Subtotal IVA 0%:</Typography>
                        <Typography>{reimbursement[FIELDS.subtotalRateZero.key] ?? "0"}</Typography>
                    </div>
                    <div className="flex justify-between">
                        <Typography>Subtotal IVA 15%:</Typography>
                        <Typography>{reimbursement['subtotalRateQuince'] ?? "0"}</Typography>
                    </div>
                    <div className="flex justify-between">
                        <Typography>IVA 15%:</Typography>
                        <Typography>{reimbursement['iva15'] ?? "0"}</Typography>
                    </div>
                    <div className="flex justify-between" style={{ fontWeight: 600, marginTop: 8 }}>
                        <Typography>Total:</Typography>
                        <Typography>{formatCurrency(getTotal())}</Typography>
                    </div>
                </div>
            </Paper>
        )
    }



    const [hasExpenses, setHasExpenses] = React.useState(false);

    React.useEffect(() => {

        const valuesToCheck = [
            reimbursement[FIELDS.subtotalNoTaxesObject.key],
            reimbursement[FIELDS.subtotalRateZero.key],
            reimbursement['subtotalRateQuince'],
            reimbursement['iva15']
        ];

        const allEmptyOrZero = valuesToCheck.every(val => {
            const numberVal = Number(val) || 0; // convierte undefined/null/string a número
            const check = numberVal === 0;
            return check;
        });
        setHasExpenses(allEmptyOrZero);
    }, [
        reimbursement[FIELDS.subtotalNoTaxesObject.key],
        reimbursement[FIELDS.subtotalRateZero.key],
        reimbursement['subtotalRateQuince'],
        reimbursement['iva15']
    ]);


    return (
        <form onSubmit={handleSubmit} className="h-full flex flex-col overflow-y-hidden">
            <div className="flex flex-col flex-grow overflow-y-hidden">
                <div className="flex flex-grow overflow-y-auto px-8 py-6">
                    <div className="flex flex-grow flex-col">
                        {
                            withTaxSupport &&
                            <FormControlLabel
                                className="w-full mb-4 pl-2"
                                control={
                                    <Switch
                                        color="primary"
                                        size="small"
                                        checked={Boolean(reimbursement[FIELDS.taxSupport.key])}
                                        onChange={(e) => {
                                            updateValues({
                                                ...reimbursement,
                                                taxSupport: e.target.checked,
                                                documentNumber: "",
                                                issuerIdentification: (e.target.checked && reimbursement.issuerIdentification) ? "" : reimbursement.issuerIdentification,
                                                receiverIdentification: (e.target.checked && reimbursement.receiverIdentification) ? supplierIdentification : reimbursement.receiverIdentification
                                            })
                                        }}
                                        name="taxSupport"
                                    />
                                }
                                label={<Typography color={Boolean(reimbursement[FIELDS.taxSupport.key]) ? "textPrimary" : "textSecondary"}>{FIELDS.taxSupport.name}</Typography>}
                            />
                        }
                        {
                            reimbursement[FIELDS.taxSupport.key] &&
                            <React.Fragment>
                                <div style={{ backgroundColor: '#fff8c9', padding: '10px', borderRadius: '10px', width: '40vw', marginBottom: '20px' }}>
                                    <Typography variant='caption' style={{ color: '#5c593e' }}>{'Se intentará buscar el comprobante electrónico autorizado en el SRI. Si la autorización del comprobante fue hace más de 30 días, o si el SRI no entrega la información del comprobante después de intentar más de dos veces, se puede intentar a cargar el archivo XML de la autorización, o llenar manualmente la información. '}</Typography>
                                </div>
                                <div className="w-full flex items-start">
                                    <TextField
                                        inputRef={descriptionInput}
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        label={FIELDS.authorizationNumber.name}
                                        required
                                        value={reimbursement[FIELDS.authorizationNumber.key] ? reimbursement[FIELDS.authorizationNumber.key].toString() : clavecceso !== undefined ? clavecceso : ''}
                                        onChange={(e) => handleChange(FIELDS.authorizationNumber.key, e.target.value)}
                                        autoFocus
                                        helperText={
                                            <div className="flex w-full justify-between">
                                                <p>{searching ? "Buscando..." : "Presiona enter para buscar en el SRI"}</p>
                                                <p className="pr-1">{`${reimbursement[FIELDS.authorizationNumber.key]?.length ?? 0}/${ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT}`}</p>
                                            </div>
                                        }
                                        FormHelperTextProps={{ style: { margin: 0, marginLeft: 8 } }}
                                        InputProps={{
                                            endAdornment: searching ?
                                                <InputAdornment position="end">
                                                    <CircularProgress color="primary" thickness={10} size={16} />
                                                </InputAdornment>
                                                : (Boolean(reimbursement[FIELDS.taxSupport.key]) && reimbursement[FIELDS.authorizationNumber.key] && AUTHORIZATION_NUMBER_SIZES.includes(reimbursement[FIELDS.authorizationNumber.key].length ?? -1)) ?
                                                    <InputAdornment position="end">
                                                        <Icon color="secondary" fontSize="small">check_circle</Icon>
                                                    </InputAdornment> : undefined
                                            ,
                                            inputComponent: AuthorizationNumberInput as any,
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault()
                                                handleSearchDocument()
                                            }
                                        }}
                                    />
                                    <Button
                                        className="mt-1"
                                        color="primary"
                                        style={{ marginLeft: 10, minWidth: 0 }}
                                        variant="contained"
                                        disableElevation={true}
                                        onClick={handleSearchDocument}
                                        disabled={reimbursement[FIELDS.authorizationNumber.key]?.length < ELECTRONIC_AUTHORIZATION_NUMBER_LIMIT || searching}
                                    >
                                        <Icon>search</Icon>
                                    </Button>
                                    <Button
                                        className="mt-1"
                                        color="primary"
                                        style={{ marginLeft: 10, minWidth: 0 }}
                                        variant="contained"
                                        disableElevation={true}
                                        onClick={() => {
                                            setOpenDialog(true)
                                        }}
                                    >
                                        <Icon>upload</Icon>
                                    </Button>
                                    <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                                        <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                                            {messageConfig.message}
                                        </Alert>
                                    </Snackbar>
                                    <FixedLoadingIndicator loading={loading} />
                                    <FileUploadDialog
                                        open={openDialog}
                                        onClose={() => { setOpenDialog(false) }}
                                        onAccept={(files) => { handleUpload(files[0].file) }}
                                        singleFile
                                        hideDescription
                                    />

                                </div>
                                <div className="w-full flex justify-between mt-4">
                                    <TextField
                                        variant="outlined"
                                        style={{ width: "49%" }}
                                        size="small"
                                        label={FIELDS.issuerIdentification.name}
                                        required
                                        value={reimbursement[FIELDS.issuerIdentification.key] ? reimbursement[FIELDS.issuerIdentification.key] : ""}
                                        onChange={(e) => { handleChange(FIELDS.issuerIdentification.key, e.target.value) }}
                                        InputProps={{ readOnly: Boolean(reimbursement[FIELDS.taxSupport.key]) }}
                                    />
                                    <TextField
                                        variant="outlined"
                                        style={{ width: "49%" }}
                                        size="small"
                                        label={FIELDS.issuerName.name}
                                        required
                                        value={reimbursement[FIELDS.issuerName.key] ? reimbursement[FIELDS.issuerName.key] : ""}
                                        onChange={(e) => handleChange(FIELDS.issuerName.key, e.target.value)}
                                        InputProps={{ readOnly: Boolean(reimbursement[FIELDS.taxSupport.key]) }}
                                    />
                                </div>
                            </React.Fragment>
                        }
                        {
                            !reimbursement[FIELDS.taxSupport.key] &&
                            <>
                                <div style={{ backgroundColor: '#fff8c9', padding: '10px', borderRadius: '10px', width: '40vw', marginBottom: '5px' }}>
                                    <Typography variant='caption' style={{ color: '#5c593e' }}>{'No olvide cargar un documento con los respaldos cuando se agregan sustento no tributarios al reembolso'}</Typography>
                                </div>
                                <React.Fragment>
                                    <div className="w-full flex justify-between mt-4">
                                        <TextField
                                            variant="outlined"
                                            style={{ width: "49%" }}
                                            size="small"
                                            label={FIELDS.issuerName.name}
                                            required
                                            value={reimbursement[FIELDS.issuerName.key] ? reimbursement[FIELDS.issuerName.key] : ""}
                                            onChange={(e) => handleChange(FIELDS.issuerName.key, e.target.value)}
                                        />
                                        <TextField
                                            variant="outlined"
                                            style={{ width: "49%" }}
                                            size="small"
                                            label={FIELDS.issuerIdentification.name}
                                            required
                                            value={reimbursement[FIELDS.issuerIdentification.key] ? reimbursement[FIELDS.issuerIdentification.key] : ""}
                                            onChange={(e) => { handleChange(FIELDS.issuerIdentification.key, e.target.value) }}
                                            InputProps={{ readOnly: Boolean(reimbursement[FIELDS.taxSupport.key]) }}
                                        />
                                    </div>

                                </React.Fragment>
                            </>
                        }
                        <div className="w-full flex justify-between mt-4">
                            <TextField
                                variant="outlined"
                                style={{ width: "49%" }}
                                size="small"
                                label={FIELDS.documentNumber.name}
                                required
                                InputProps={{
                                    readOnly: Boolean(reimbursement[FIELDS.taxSupport.key]),
                                    inputComponent: Boolean(reimbursement[FIELDS.taxSupport.key]) ? InvoiceInput as any : undefined,
                                }}
                                value={reimbursement[FIELDS.documentNumber.key] ? reimbursement[FIELDS.documentNumber.key] : ""}
                                onChange={(e) => handleChange(FIELDS.documentNumber.key, e.target.value)}
                            />
                            <KeyboardDatePicker
                                style={{ width: "49%" }}
                                className="date-input"
                                autoOk
                                invalidDateMessage={FIELDS.invalidDate.name}
                                InputAdornmentProps={{ position: "start", style: { margin: 0 } }}
                                value={reimbursement[FIELDS.emissionDate.key] ?? null}
                                onChange={(date) => handleChange(FIELDS.emissionDate.key, date)}
                                inputVariant="outlined"
                                fullWidth
                                emptyLabel={FIELDS.emptyDate.name}
                                required
                                size="small"
                                label={FIELDS.emissionDate.name}
                                variant="inline"
                                format="dd/MM/yyyy"
                                TextFieldComponent={(props) => (
                                    <TextField
                                        {...props}
                                        InputProps={{
                                            ...props.InputProps,
                                            readOnly: Boolean(
                                                reimbursement[FIELDS.taxSupport.key]
                                            )
                                        }}
                                    />
                                )}
                                KeyboardButtonProps={{
                                    disabled: Boolean(
                                        reimbursement[FIELDS.taxSupport.key]
                                    )
                                }}
                            />
                        </div>
                        {
                            withExpenses &&
                            <div className="w-full flex justify-between mt-4">
                                {Boolean(reimbursement[FIELDS.taxSupport.key]) && (
                                    <>
                                        <TextField
                                            variant="outlined"
                                            style={{ width: "49%" }}
                                            size="small"
                                            label={FIELDS.receiverIdentification.name}
                                            required
                                            value={reimbursement[FIELDS.receiverIdentification.key] || ""}
                                            onChange={(e) =>
                                                handleChange(
                                                    FIELDS.receiverIdentification.key,
                                                    e.target.value
                                                )
                                            }
                                            InputProps={{ readOnly: true }}
                                        />

                                        <TextField
                                            variant="outlined"
                                            style={{ width: "49%" }}
                                            size="small"
                                            label={FIELDS.receiverName.name}
                                            required
                                            value={reimbursement[FIELDS.receiverName.key] || ""}
                                            onChange={(e) =>
                                                handleChange(
                                                    FIELDS.receiverName.key,
                                                    e.target.value
                                                )
                                            }
                                            InputProps={{ readOnly: true }}
                                        />
                                    </>
                                )}
                            </div>
                        }
                        {
                            (
                                reimbursement[FIELDS.expenses.key]?.key === 'OTHER (DETAIL)' ||
                                reimbursement[FIELDS.expenses.key]?.key === 'Personal Meals'
                            ) &&
                            <div className="w-full flex justify-between mt-4">
                                <TextField
                                    variant="outlined"
                                    style={{ width: "49%" }}
                                    size="small"
                                    required
                                    label={FIELDS.description.name}
                                    value={reimbursement[FIELDS.description.key] || ""}
                                    onChange={(e) =>
                                        handleChange(FIELDS.description.key, e.target.value)
                                    }
                                />
                            </div>
                        }
                        <div className="w-full pt-4 pl-2">
                            <Typography style={{ fontWeight: 900 }} color="textSecondary" variant="caption">{"Valores"}</Typography>
                        </div>
                        <div className="w-full flex justify-between mt-4">
                            <TextField
                                variant="outlined"
                                style={{ width: "49%" }}
                                size="small"
                                label={FIELDS.subtotalNoTaxesObject.name}
                                required
                                onFocus={event => { event.target.select() }}
                                value={reimbursement[FIELDS.subtotalNoTaxesObject.key] ? reimbursement[FIELDS.subtotalNoTaxesObject.key] : "0"}
                                onChange={(e) => {

                                    const updatedValues = {
                                        ...reimbursement,
                                        subtotalNoTaxesObject: e.target.value
                                    }

                                    if (!validateTotalLimit(updatedValues)) {
                                        return
                                    }

                                    handleChange(FIELDS.subtotalNoTaxesObject.key, e.target.value)
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Icon fontSize="small" color="action">attach_money</Icon>
                                        </InputAdornment>
                                    ),
                                    inputComponent: CurrencyInput as any,
                                }}
                            />
                            {reimbursement[FIELDS.taxSupport.key] && (
                                <TextField
                                    variant="outlined"
                                    style={{ width: "49%" }}
                                    size="small"
                                    label={FIELDS.subtotalRateZero.name}
                                    required
                                    onFocus={event => { event.target.select() }}
                                    value={reimbursement[FIELDS.subtotalRateZero.key] ? reimbursement[FIELDS.subtotalRateZero.key] : "0"}
                                    onChange={(e) => {

                                        const updatedValues = {
                                            ...reimbursement,
                                            subtotalRateZero: e.target.value
                                        }

                                        if (!validateTotalLimit(updatedValues)) {
                                            return
                                        }

                                        handleChange(FIELDS.subtotalRateZero.key, e.target.value)
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon fontSize="small" color="action">attach_money</Icon>
                                            </InputAdornment>
                                        ),
                                        inputComponent: CurrencyInput as any,
                                    }}
                                />
                            )}
                        </div>

                        {reimbursement[FIELDS.taxSupport.key] && (
                            <div className="w-full flex justify-between mt-4">
                                <TextField
                                    variant="outlined"
                                    style={{ width: "49%" }}
                                    size="small"
                                    onFocus={event => { event.target.select() }}
                                    label={'Subtotal IVA 15%'}
                                    required
                                    value={reimbursement['subtotalRateQuince'] ? reimbursement['subtotalRateQuince'] : "0"}
                                    onChange={(e) => {

                                        const updatedValues = {
                                            ...reimbursement,
                                            subtotalRateQuince: e.target.value,
                                            iva15: (parseFloat(e.target.value || "0") * 0.15).toFixed(2)
                                        }

                                        if (!validateTotalLimit(updatedValues)) {
                                            return
                                        }

                                        updateValues(updatedValues)
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon fontSize="small" color="action">attach_money</Icon>
                                            </InputAdornment>
                                        ),
                                        inputComponent: CurrencyInput as any,
                                    }}
                                />
                                <TextField
                                    variant="outlined"
                                    style={{ width: "49%" }}
                                    size="small"
                                    label={"IVA 15%"}
                                    required
                                    onFocus={event => { event.target.select() }}
                                    value={reimbursement['iva15'] ? reimbursement['iva15'] : "0"}
                                    onChange={(e) => {

                                        const updatedValues = {
                                            ...reimbursement,
                                            iva15: e.target.value
                                        }

                                        if (!validateTotalLimit(updatedValues)) {
                                            return
                                        }

                                        handleChange('iva15', e.target.value)
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon fontSize="small" color="action">attach_money</Icon>
                                            </InputAdornment>
                                        ),
                                        inputComponent: CurrencyInput as any,
                                    }}
                                />
                            </div>
                        )}
                        <Paper className="px-4 py-2 mt-4 flex flex-col w-full" variant="outlined">
                            <Typography
                                align="right"
                                style={{ fontWeight: 600, letterSpacing: 0.85 }}
                                variant="button"
                            >
                                {`Total: ${formatCurrency(getTotal())}`}
                            </Typography>
                        </Paper>

                    </div>
                    {
                        withExpenses &&
                        <div className="ml-8" style={{ width: "50%" }}>
                            <Autocomplete
                                options={
                                    isCCH
                                        ? EXPENSE_ITEMS.filter(item => item.key !== "ENTERTAINMENT")
                                        : EXPENSE_ITEMS
                                }
                                getOptionLabel={(option) => option.name}
                                size="small"
                                openOnFocus
                                disabled={hasExpenses}
                                fullWidth
                                noOptionsText="No se encontraron gastos registrados..."
                                value={reimbursement[FIELDS.expenses.key] ?? null} // objeto o null
                                onChange={(_, newValue) => {
                                    // newValue es un solo objeto o null
                                    handleChange(FIELDS.expenses.key, newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={FIELDS.expenses.name}
                                        variant="outlined"
                                        required // obligatorio
                                    />
                                )}
                                renderOption={(option) => (
                                    <Typography variant="subtitle2">{option.name}</Typography>
                                )}
                                disableClearable // evita que el usuario borre el valor
                            />
                            {withExpenses && reimbursement[FIELDS.expenses.key] && <ExpensesSummary />}
                        </div>
                    }

                </div>
            </div>
            <div className="w-full flex justify-end pr-8 pb-6">
                {
                    !edit &&
                    <FormGroup className="p-2" row>
                        <FormControlLabel
                            control={<Switch color="primary" size="small" checked={closeOnAdd} onChange={(e) => setCloseOnAdd(e.target.checked)} />}
                            label={"Cerrar al agregar"}
                        />
                    </FormGroup>
                }
                <Button
                    color="primary"
                    variant="contained"
                    disableElevation
                    type="submit"
                >
                    {edit ? "Editar" : "Agregar"}
                </Button>
            </div>
            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                    {messageConfig.message}
                </Alert>
            </Snackbar>
        </form>
    )
}

export default ReimbursementForm
